// =============================================================================
// MeuDriver — Editor de Disponibilidade Semanal do Instrutor (H1)
// =============================================================================

import { useState } from 'react'
import { format, addDays } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Plus, Trash2, CalendarOff, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/hooks/useAuth'
import {
  useInstructorAvailability,
  useBlockedSlots,
  useManageAvailability,
} from '@/hooks/useAvailability'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import type { InstructorAvailabilityRow } from '@/types/database'

// ─── Constantes ───────────────────────────────────────────────────────────────

const DAY_LABELS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']

/** Horários disponíveis para seleção: 06h–22h de 1 em 1 hora */
function buildHourOptions(): string[] {
  return Array.from({ length: 17 }, (_, i) => {
    const h = i + 6
    return `${String(h).padStart(2, '0')}:00`
  })
}

const HOUR_OPTIONS = buildHourOptions()

// ─── WeeklyGrid ───────────────────────────────────────────────────────────────

interface DayRowProps {
  dow: number
  slots: InstructorAvailabilityRow[]
  instructorId: string
  onAdd: (dow: number, start: string, end: string) => void
  onRemove: (id: string) => void
  isPending: boolean
}

function DayRow({ dow, slots, onAdd, onRemove, isPending }: DayRowProps) {
  const [adding, setAdding] = useState(false)
  const [newStart, setNewStart] = useState('08:00')
  const [newEnd, setNewEnd] = useState('09:00')

  function handleAdd() {
    if (newStart >= newEnd) {
      toast.error('O horário de início deve ser anterior ao fim.')
      return
    }
    onAdd(dow, newStart + ':00', newEnd + ':00')
    setAdding(false)
    setNewStart('08:00')
    setNewEnd('09:00')
  }

  return (
    <div className="border rounded-xl p-4 space-y-3 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-sm text-gray-900 dark:text-gray-100">
          {DAY_LABELS[dow]}
        </h4>
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1"
          onClick={() => setAdding((v) => !v)}
          disabled={isPending}
        >
          <Plus className="h-3 w-3" />
          Adicionar
        </Button>
      </div>

      {/* Slots existentes */}
      {slots.length === 0 && !adding && (
        <p className="text-xs text-muted-foreground">Nenhum horário configurado</p>
      )}
      <div className="flex flex-wrap gap-2">
        {slots.map((s) => (
          <div
            key={s.id}
            className="flex items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 dark:border-brand-800 dark:bg-brand-950 px-2.5 py-1"
          >
            <span className="text-xs font-semibold text-brand-700 dark:text-brand-300">
              {s.start_time.slice(0, 5)} – {s.end_time.slice(0, 5)}
            </span>
            <button
              type="button"
              onClick={() => onRemove(s.id)}
              disabled={isPending}
              className="text-brand-400 hover:text-red-500 transition-colors"
              aria-label="Remover horário"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>

      {/* Formulário inline de adição */}
      {adding && (
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs">Início</Label>
            <select
              value={newStart}
              onChange={(e) => setNewStart(e.target.value)}
              className="h-8 rounded-md border border-input bg-background text-sm px-2 focus:outline-none focus:ring-2 focus:ring-ring dark:bg-gray-900 dark:border-gray-700"
            >
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Fim</Label>
            <select
              value={newEnd}
              onChange={(e) => setNewEnd(e.target.value)}
              className="h-8 rounded-md border border-input bg-background text-sm px-2 focus:outline-none focus:ring-2 focus:ring-ring dark:bg-gray-900 dark:border-gray-700"
            >
              {HOUR_OPTIONS.map((h) => (
                <option key={h} value={h}>{h}</option>
              ))}
            </select>
          </div>
          <Button size="sm" className="h-8" onClick={handleAdd} disabled={isPending}>
            Salvar
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={() => setAdding(false)}
          >
            Cancelar
          </Button>
        </div>
      )}
    </div>
  )
}

// ─── BlockDialog ──────────────────────────────────────────────────────────────

interface BlockDialogProps {
  open: boolean
  onOpenChange: (v: boolean) => void
  instructorId: string
  onBlock: (date: string, reason: string) => void
  isPending: boolean
}

function BlockDialog({ open, onOpenChange, onBlock, isPending }: BlockDialogProps) {
  const [date, setDate] = useState('')
  const [reason, setReason] = useState('')
  const today = new Date().toISOString().slice(0, 10)

  function handleConfirm() {
    if (!date) {
      toast.error('Selecione uma data.')
      return
    }
    onBlock(date, reason)
    setDate('')
    setReason('')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Bloquear data</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="block-date">Data</Label>
            <Input
              id="block-date"
              type="date"
              min={today}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="block-reason">Motivo (opcional)</Label>
            <Input
              id="block-reason"
              placeholder="Ex: férias, compromisso pessoal..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={isPending || !date}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Bloquear dia inteiro
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── AvailabilityEditor ───────────────────────────────────────────────────────

export function AvailabilityEditor() {
  const { user } = useAuth()
  const instructorId = user?.id ?? ''

  const { data: slots = [], isLoading: loadingSlots } = useInstructorAvailability(instructorId)
  const { data: blocked = [], isLoading: loadingBlocked } = useBlockedSlots(instructorId)
  const { upsertSlot, removeSlot, addBlock, removeBlock } = useManageAvailability()

  const [blockOpen, setBlockOpen] = useState(false)

  // Agrupa slots por day_of_week
  const slotsByDay = Array.from({ length: 7 }, (_, i) =>
    slots.filter((s) => s.day_of_week === i && s.active),
  )

  function handleAdd(dow: number, start: string, end: string) {
    if (!instructorId) return
    upsertSlot.mutate({
      instructor_id: instructorId,
      day_of_week: dow,
      start_time: start,
      end_time: end,
      active: true,
    })
  }

  function handleRemove(id: string) {
    removeSlot.mutate(id)
  }

  function handleBlock(date: string, reason: string) {
    if (!instructorId) return
    addBlock.mutate(
      { instructor_id: instructorId, blocked_date: date, reason: reason || null },
      { onSuccess: () => setBlockOpen(false) },
    )
  }

  const isMutating =
    upsertSlot.isPending || removeSlot.isPending || addBlock.isPending || removeBlock.isPending

  return (
    <div className="space-y-6">
      {/* Grade semanal */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">Horários disponíveis por semana</CardTitle>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => setBlockOpen(true)}
            data-testid="btn-bloquear-data"
          >
            <CalendarOff className="h-3.5 w-3.5" />
            Bloquear data
          </Button>
        </CardHeader>
        <CardContent>
          {loadingSlots ? (
            <div className="space-y-3">
              {Array.from({ length: 7 }).map((_, i) => (
                <Skeleton key={i} className="h-16 w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {Array.from({ length: 7 }, (_, dow) => (
                <DayRow
                  key={dow}
                  dow={dow}
                  slots={slotsByDay[dow]}
                  instructorId={instructorId}
                  onAdd={handleAdd}
                  onRemove={handleRemove}
                  isPending={isMutating}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bloqueios pontuais */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Datas bloqueadas (próximos 90 dias)</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingBlocked ? (
            <Skeleton className="h-20 w-full rounded-xl" />
          ) : blocked.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhuma data bloqueada.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {blocked.map((b) => (
                <div
                  key={b.id}
                  className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950 px-3 py-1.5"
                >
                  <CalendarOff className="h-3.5 w-3.5 text-red-500" />
                  <span className="text-xs font-semibold text-red-700 dark:text-red-300">
                    {format(new Date(b.blocked_date + 'T12:00'), 'dd MMM yyyy', { locale: ptBR })}
                  </span>
                  {b.reason && (
                    <Badge variant="outline" className="text-[10px] h-4 px-1 border-red-200 text-red-600">
                      {b.reason}
                    </Badge>
                  )}
                  <button
                    type="button"
                    onClick={() => removeBlock.mutate(b.id)}
                    disabled={isMutating}
                    className="text-red-400 hover:text-red-600 transition-colors"
                    aria-label="Remover bloqueio"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <BlockDialog
        open={blockOpen}
        onOpenChange={setBlockOpen}
        instructorId={instructorId}
        onBlock={handleBlock}
        isPending={addBlock.isPending}
      />
    </div>
  )
}
